
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import {addComma} from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import moment from 'moment';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
@Component({
    components: {
        TableFilterable,
    },
})
export default class SalesOpenQuotationsTrackingTable extends FilterWidget {
    @Prop({default: () => []}) public selectedAreas!: string[];

    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'quotations'];
    }

    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
    > = [];

    public validityDays: number = 60;

    public tableItems: Array<{
        agent: string;
        quote_count: number;
        total_quoted_amt: number;
        converted_quote_count: number;
        open_quote_count: number;
        converted_amount: number;
        conversion_rate: number;
        items: any;
    }> = [];
    public tableFields: any[] = [];
    public detailFields: any[] = [];
    public detailFields2: any[] = [];

    public isCalculateGrouping: number = 0;

    public infoModal = {
        id: 'info-modal',
        index: 0,
        title: '',
        extras: '',
        amount: '',
        items: [] as any[],
    };

    public format(
        value: number,
        decimal: number = 2,
        forceDecimal: boolean = true,
    ) {
        return addComma(value, decimal, forceDecimal);
    }

    public generateGrouping() {
        this.isCalculateGrouping += 1;
    }

    public info(item, extras, index, title, amount, link) {
        this.infoModal.title = title;
        this.infoModal.extras = extras;
        this.infoModal.index = index;
        this.infoModal.amount = this.format(amount);
        this.infoModal.items = item.items;
        this.$root.$emit('bv::show::modal', this.infoModal.id, link);
    }

    public resetInfoModal() {
        this.infoModal.title = '';
        this.infoModal.extras = '';
        this.infoModal.items = [];
    }

    public get currency() {
        return gdbx.currencySymbol;
    }

    public get exportFileName() {
        return 'Open Quotations Tracking' + '_' + 'Validity Within 60 Days';
    }

    public get expensiveHook() {
        const {
            selectedAgents,
            isCalculateGrouping,
            selectedCustomers,
            selectedAreas,
        } = this;
        return JSON.stringify([
            selectedAgents,
            isCalculateGrouping,
            selectedCustomers,
            selectedAreas,
        ]);
    }

    public async loadDetailForDoc(docNo: string): Promise<any[]> {
        const queryHead = await this._ensureTablesLoaded(['quotations', 'globalCustomers']);

        const quotationsTable = getTableName('quotations');
        const escaped = docNo.replace(/'/g, '\'\'');
        const lineData = await queryHead.rawQuery(`
            SELECT TRY_CAST("SEQ" AS DOUBLE) as seq,
                ${COL.ITEMCODE} as itemCode,
                TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                TRY_CAST(${COL.QUANTITY} AS DOUBLE) as quantity,
                TRY_CAST(${COL.DISCOUNT} AS DOUBLE) as discount
            FROM ${quotationsTable}
            WHERE ${COL.DOCNO} = '${escaped}'
        `);

        return lineData.map((row: any) => {
            const amt = Number(row.amount) || 0;
            const qty = Number(row.quantity) || 0;
            return {
                sequence: (Number(row.seq) || 0) / 1000,
                item: gdbx.stockItemNames[String(row.itemCode)],
                unit_price: qty ? amt / qty : 0,
                discount: Math.ceil(Number(row.discount) || 0),
                quantity: qty,
                sub_total: amt,
            };
        });
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        // Filter quotations within validity period
        const cutoff = moment()
            .subtract(this.validityDays, 'days').valueOf();
        const quotationsTable = getTableName('quotations');

        // Ensure all cross-table dependencies are loaded
        const queryHead = await this._ensureTablesLoaded(['quotations', 'globalCustomers']);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Build filter clauses manually
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${cutoff} AND ${Date.now()}`;

        let agentFilter = '';
        if (this.selectedAgents.length > 0) {
            const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
            agentFilter = ` AND ${COL.AGENT} IN (${list})`;
        }

        const globalCustomersTable = getTableName('globalCustomers');
        let filterClause = '';

        if (this.selectedAreas.length > 0) {
            const areaList = this.selectedAreas.map((a) => `'${escapeStr(a)}'`).join(',');
            filterClause += ` AND ${COL.CODE} IN (
                SELECT "CODE" FROM ${globalCustomersTable} WHERE "AREA" IN (${areaList})
            )`;
        }

        if (this.selectedCustomers.length > 0) {
            const customerList = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
            filterClause += ` AND ${COL.CODE} IN (${customerList})`;
        }

        // Per-doc aggregation: classify open vs converted (line items loaded on click)
        const docData = await queryHead.rawQuery(`
            SELECT ${COL.AGENT} as agent, ${COL.DOCNO} as docNo,
                MIN(${COL.CODE}) as code,
                MIN(TRY_CAST(${COL.DOCDATE} AS BIGINT)) as docDate,
                SUM(TRY_CAST(${COL.AMOUNT} AS DOUBLE)) as amount,
                SUM(TRY_CAST("TRANSFERREDAMT" AS DOUBLE)) as transferredAmt,
                SUM(TRY_CAST("OSQTY" AS DOUBLE)) as totalOsQty,
                SUM(TRY_CAST(${COL.QUANTITY} AS DOUBLE)) as totalQty
            FROM ${quotationsTable}
            WHERE ${dateFilter}${agentFilter}${filterClause}
            GROUP BY ${COL.AGENT}, ${COL.DOCNO}
        `);

        // Group docs by agent, classify open vs converted
        const agentMap: Map<string, {
            totalAmount: number;
            convertedAmount: number;
            quoteCount: number;
            openCount: number;
            convertedCount: number;
            openDocs: any[];
        }> = new Map();

        for (const row of docData) {
            const agent = String(row.agent);
            const docNo = String(row.docNo);
            const totalOsQty = Number(row.totalOsQty) || 0;
            const totalQty = Number(row.totalQty) || 0;
            const amount = Number(row.amount) || 0;
            const transferredAmt = Number(row.transferredAmt) || 0;
            const isOpen = totalOsQty === totalQty;

            if (!agentMap.has(agent)) {
                agentMap.set(agent, {
                    totalAmount: 0, convertedAmount: 0,
                    quoteCount: 0, openCount: 0,
                    convertedCount: 0, openDocs: [],
                });
            }

            const agentData = agentMap.get(agent)!;
            agentData.totalAmount += amount;
            agentData.convertedAmount += transferredAmt;
            agentData.quoteCount++;

            if (isOpen) {
                agentData.openCount++;
                const code = String(row.code);
                agentData.openDocs.push({
                    open_quote_doc_no: docNo,
                    extras: `- ${moment(Number(row.docDate)).format('DD/MM/YYYY')}
						- ${gdbx.customerNames[code]}
						- ${agent}`,
                    date: Number(row.docDate),
                    company: gdbx.customerNames[code],
                    amount,
                    items: [],
                });
            } else {
                agentData.convertedCount++;
            }
        }

        // Build table items from agent aggregates
        const result: any[] = [];
        for (const [agent, data] of agentMap) {
            if (data.openCount <= 0) { continue; }
            result.push({
                agent,
                quote_count: data.quoteCount,
                total_quoted_amt: data.totalAmount,
                converted_quote_count: data.convertedCount,
                open_quote_count: data.openCount,
                converted_amount: data.convertedAmount,
                conversion_rate: data.quoteCount
                    ? (data.convertedCount / data.quoteCount) * 100
                    : 0,
                items: data.openDocs,
            });
        }
        this.generateLoadingText(0.95);

        this.tableItems = result;

        this.tableFields = [
            {key: 'agent', sortable: true},
            {
                key: 'open_quote_count',
                label: 'Open Quote',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'conversion_rate',
                label: 'Conversion (Case)',
                sortable: true,
                formatter: TableItemFormatter.percentage,
            },
            {
                key: 'quote_count',
                label: 'Total Quote',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'converted_quote_count',
                label: 'Converted Quote',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'total_quoted_amt',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },

            {
                key: 'converted_amount',
                label: 'Converted Amt',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },


        ];

        this.detailFields = [
            {
                key: 'open_quote_doc_no',
                label: 'Doc No',
                sortable: true,
            },
            {
                key: 'date',
                sortable: true,
                formatter: TableItemFormatter.date3,
            },
            {
                key: 'company',
                sortable: true,
            },
            {
                key: 'amount',
                label: 'Amt',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },
        ];

        this.detailFields2 = [
            {key: 'sequence', label: 'Seq', sortable: true, formatter: TableItemFormatter.basicInteger},
            {key: 'item', sortable: true},
            {
                key: 'quantity',
                label: 'Qty',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'unit_price',
                label: 'Unit Price',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'discount',
                label: 'Discount',
                sortable: true,
                formatter: TableItemFormatter.basicInteger,
            },
            {
                key: 'sub_total',
                sortable: true,
                formatter: TableItemFormatter.currencySplit,
            },
        ];

        this.saveHistory('tableItems', 'tableFields', 'detailFields', 'detailFields2');
    }
}
